import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { getSetupGuideState, getSystemModules, dismissSetupGuide, skipSetupStep } from '../../core/_requests';
import { deriveGuide } from './deriveGuide';
import { isGuideHidden, currentGuideIndex } from './guideFormat';
import { useGettingStartedWidget } from './GettingStartedWidgetContext';
import GuidePanelBody from './GuidePanelBody';
import { useLocation } from 'react-router-dom';
import MetronicCard from '@/app/components/layout/MetronicCard';

const STATE_QUERY_KEY = ['home-setup-guide-state'];
const MODULES_QUERY_KEY = ['system-modules'];
const INTERVAL_CHECK_TIME = 5000;

const GettingStartedWidget = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { isClosed, isCollapsed, close, toggleCollapsed } = useGettingStartedWidget();
  const [locallyDismissed, setLocallyDismissed] = useState(false);
  const [isDesktopSidebar, setIsDesktopSidebar] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 992px)').matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(min-width: 992px)');
    const handleChange = (event: MediaQueryListEvent) => setIsDesktopSidebar(event.matches);

    mediaQuery.addEventListener?.('change', handleChange);
    mediaQuery.addListener?.(handleChange);

    return () => {
      mediaQuery.removeEventListener?.('change', handleChange);
      mediaQuery.removeListener?.(handleChange);
    };
  }, []);

  const { data: stateResponse } = useQuery({
    queryKey: STATE_QUERY_KEY,
    queryFn: getSetupGuideState,
    meta: { suppressGlobalError: true },
  });

  const { data: modulesResponse } = useQuery({
    queryKey: MODULES_QUERY_KEY,
    queryFn: getSystemModules,
    meta: { suppressGlobalError: true },
  });

  const guide = useMemo(() => {
    if (!stateResponse?.data) return undefined;
    return deriveGuide(stateResponse.data, modulesResponse?.data);
  }, [stateResponse?.data, modulesResponse?.data]);

  const isHidden = useMemo(
    () => isClosed || locallyDismissed || isGuideHidden(guide),
    [guide, isClosed, locallyDismissed],
  );

  const dismissMutation = useMutation({
    mutationFn: dismissSetupGuide,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STATE_QUERY_KEY }),
    onError: (error) => {
      setLocallyDismissed(false);
      SystemNotification.error(getErrorMessage(error));
    },
  });

  const skipMutation = useMutation({
    mutationFn: skipSetupStep,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STATE_QUERY_KEY }),
    onError: (error) => SystemNotification.error(getErrorMessage(error)),
  });

  useEffect(() => {
    if (isHidden) return;

    // Immediately refresh guide state on relevant route changes, as the user may have
    // navigated to a different part of the app where the guide state is relevant.
    queryClient.invalidateQueries({ queryKey: STATE_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MODULES_QUERY_KEY });
  }, [isHidden, queryClient, location.pathname]);

  useEffect(() => {
    if (isHidden) return;

    // Periodically refresh guide state, as the user may have completed steps in another tab.
    const intervalId = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: STATE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MODULES_QUERY_KEY });
    }, INTERVAL_CHECK_TIME);

    return () => clearInterval(intervalId);
  }, [isHidden, queryClient]);

  if (!guide || isHidden) return null;

  const idx = currentGuideIndex(guide);
  if (idx === -1) return null;
  const level = guide.levels[idx];

  const handleSkipAll = () => {
    const codes = level.groups.flatMap((group) => group.steps.map((step) => step.code));
    dismissMutation.mutate(codes);
  };

  return (
    <MetronicCard
      className="shadow-sm position-fixed"
      data-testid="getting-started-widget"
      style={{
        width: 'min(320px, calc(100vw - 3rem))',
        zIndex: 100,
        bottom: '1.5rem',
        left: isDesktopSidebar ? 'calc(var(--bs-app-sidebar-width-actual, 265px) + 1.5rem)' : '1.5rem',
      }}
    >
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
        <span className="fw-bold text-gray-900">Primeiros passos</span>
        <div className="d-flex align-items-center gap-2">
          <button
            aria-label="Minimizar"
            className="btn btn-icon btn-sm btn-active-light"
            type="button"
            onClick={toggleCollapsed}
          >
            <i className={`ki-duotone ${isCollapsed ? 'ki-arrow-up' : 'ki-arrow-down'} fs-4`}>
              <span className="path1" />
              <span className="path2" />
            </i>
          </button>
          <button aria-label="Fechar" className="btn btn-icon btn-sm btn-active-light" type="button" onClick={close}>
            <i className="ki-duotone ki-cross fs-3">
              <span className="path1" />
              <span className="path2" />
            </i>
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <GuidePanelBody
          environmentId={currentUser?.environment_id ?? ''}
          level={level}
          onSkipAll={handleSkipAll}
          onSkipStep={(code) => skipMutation.mutate(code)}
        />
      )}
    </MetronicCard>
  );
};

export default GettingStartedWidget;
